/**
 * Page-relative rectangle maths, shared by every field-mapping surface.
 *
 * Every rectangle in the document system is four numbers in [0,1] measured against that page's own
 * dimensions with the origin at the TOP-LEFT. The admin overlay, the public signing page and the
 * server-side PDF renderer all read the same four numbers, so this conversion lives in one place.
 */

export function clamp(value) {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function normalizeRect(startX, startY, endX, endY) {
  return {
    xPct: Math.min(startX, endX),
    yPct: Math.min(startY, endY),
    widthPct: Math.abs(endX - startX),
    heightPct: Math.abs(endY - startY),
  };
}

/** Pointer event → page-relative {x, y}, clamped to the page. */
export function pointerToPercent(event, element) {
  const bounds = element.getBoundingClientRect();
  return {
    x: clamp((event.clientX - bounds.left) / bounds.width),
    y: clamp((event.clientY - bounds.top) / bounds.height),
  };
}

export function formatRect(field) {
  const at = (value) => Number(value || 0).toFixed(3);
  return `x ${at(field.xPct)}, y ${at(field.yPct)}, w ${at(field.widthPct)}, h ${at(
    field.heightPct
  )}`;
}

/**
 * Smallest drag, on each axis, that counts as drawing a rectangle rather than clicking.
 * At A4 width this is roughly 3 pt, far below any usable field and far above pointer jitter.
 */
export const MIN_DRAW_PCT = 0.005;

/**
 * Whether a finished drag should be stored as a field rectangle.
 *
 * A plain click produces a zero-size rectangle, and clicking an already-drawn field box to select
 * it still sends mousedown/mouseup to the drawing layer underneath. Without this guard that click
 * is reported as a completed draw and overwrites the *currently selected* field with a 0x0
 * rectangle, silently unmapping it. Both axes must clear the threshold: a purely horizontal or
 * vertical drag yields a rectangle with no area, which no renderer can stamp into.
 */
export function isDrawnRect(rect) {
  if (!rect) return false;
  return rect.widthPct >= MIN_DRAW_PCT && rect.heightPct >= MIN_DRAW_PCT;
}
